// src/api/messaging/conversationsApi.ts
import { ENV } from '../../config/runtimeEnv';
import {
	Conversation,
	CreatePatientQueueRequest,
	CreateDirectConversationRequest,
	CreateGroupConversationRequest,
	AddParticipantRequest,
	RemoveParticipantRequest,
	AssignConversationRequest,
	ClaimConversationRequest,
	CloseConversationRequest,
	GetConversationsParams,
	ConversationListResponse,
	ApiResponse,
	ConversationParticipant,
	ParticipantListResponse,
} from '../../types/messaging';
import type { UserUuid } from '../../types/identity';
import { createServiceClient } from '../../lib/http/client';

export const API_BASE_URL = ENV.VITE_NOTIFICATION_BASE_URL;

const createApiInstance = () => {
	return createServiceClient({
		baseURL: API_BASE_URL,
		headers: {
			'Content-Type': 'application/json',
		},
	});
};

/**
 * Get all conversations for a user
 */
export const getConversations = async (params: GetConversationsParams): Promise<Conversation[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<ConversationListResponse>('/conversations', { params });
		return response.data.data;
	} catch (error) {
		console.error('Error fetching conversations:', error);
		throw error;
	}
};

/**
 * Get a specific conversation by ID
 */
export const getConversation = async (conversationId: string, userId: UserUuid): Promise<Conversation> => {
	try {
		const api = createApiInstance();
		const response = await api.get<ApiResponse<Conversation>>(`/conversations/${conversationId}`, {
			params: { user_id: userId },
		});
		return response.data.data!;
	} catch (error) {
		console.error('Error fetching conversation:', error);
		throw error;
	}
};

/**
 * Get all conversations in the clinic patient queue
 */
export const getQueueConversations = async (): Promise<Conversation[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<ConversationListResponse>('/conversations/queue');
		return response.data.data;
	} catch (error) {
		console.error('Error fetching queue conversations:', error);
		throw error;
	}
};

/**
 * Create a patient queue conversation (patient initiates)
 */
export const createPatientQueueConversation = async (
	request: CreatePatientQueueRequest
): Promise<Conversation> => {
	try {
		const api = createApiInstance();
		const response = await api.post<ApiResponse<Conversation>>(
			'/conversations/patient-queue',
			request
		);
		return response.data.data!;
	} catch (error) {
		console.error('Error creating patient queue conversation:', error);
		throw error;
	}
};

/**
 * Create a direct conversation (practitioner to patient)
 */
export const createDirectConversation = async (
	request: CreateDirectConversationRequest
): Promise<Conversation> => {
	try {
		const api = createApiInstance();
		const response = await api.post<ApiResponse<Conversation>>('/conversations/direct', request);
		return response.data.data!;
	} catch (error) {
		console.error('Error creating direct conversation:', error);
		throw error;
	}
};

/**
 * Create a group conversation
 */
export const createGroupConversation = async (
	request: CreateGroupConversationRequest
): Promise<Conversation> => {
	try {
		const api = createApiInstance();
		const response = await api.post<ApiResponse<Conversation>>('/conversations/group', request);
		return response.data.data!;
	} catch (error) {
		console.error('Error creating group conversation:', error);
		throw error;
	}
};

/**
 * Add a participant to a conversation
 */
export const addParticipant = async (
	conversationId: string,
	request: AddParticipantRequest
): Promise<ConversationParticipant> => {
	try {
		const api = createApiInstance();
		const response = await api.post<ApiResponse<ConversationParticipant>>(
			`/conversations/${conversationId}/participants`,
			request
		);
		return response.data.data!;
	} catch (error) {
		console.error('Error adding participant:', error);
		throw error;
	}
};

/**
 * Remove a participant from a conversation
 */
export const removeParticipant = async (
	conversationId: string,
	request: RemoveParticipantRequest
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.delete(`/conversations/${conversationId}/participants`, { data: request });
	} catch (error) {
		console.error('Error removing participant:', error);
		throw error;
	}
};

/**
 * Get all participants in a conversation
 */
export const getParticipants = async (conversationId: string, userId: UserUuid): Promise<ConversationParticipant[]> => {
	try {
		const api = createApiInstance();
		const response = await api.get<ParticipantListResponse>(
			`/conversations/${conversationId}/participants`,
			{ params: { user_id: userId } }
		);
		return response.data.data;
	} catch (error) {
		console.error('Error fetching participants:', error);
		throw error;
	}
};

/**
 * Assign a conversation to a specific practitioner
 */
export const assignConversation = async (
	conversationId: string,
	request: AssignConversationRequest
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/assign`, request);
	} catch (error) {
		console.error('Error assigning conversation:', error);
		throw error;
	}
};

/**
 * Claim a conversation from the queue
 */
export const claimConversation = async (
	conversationId: string,
	request: ClaimConversationRequest
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/claim`, request);
	} catch (error) {
		console.error('Error claiming conversation:', error);
		throw error;
	}
};

/**
 * Close a conversation (practitioners only)
 */
export const closeConversation = async (
	conversationId: string,
	request: CloseConversationRequest
): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/close`, request);
	} catch (error) {
		console.error('Error closing conversation:', error);
		throw error;
	}
};

/**
 * Archive a conversation
 */
export const archiveConversation = async (conversationId: string, userId: UserUuid): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/archive`, { user_id: userId });
	} catch (error) {
		console.error('Error archiving conversation:', error);
		throw error;
	}
};

/**
 * Unarchive a conversation
 */
export const unarchiveConversation = async (conversationId: string, userId: UserUuid): Promise<void> => {
	try {
		const api = createApiInstance();
		await api.post(`/conversations/${conversationId}/unarchive`, { user_id: userId });
	} catch (error) {
		console.error('Error unarchiving conversation:', error);
		throw error;
	}
};


/**
 * Helper: Find or create a direct conversation
 */
export const findOrCreateDirectConversation = async (
	initiatorUserId: UserUuid,
	otherUserId: UserUuid
): Promise<Conversation> => {
	try {
		// Try to find existing conversation first
		const conversations = await getConversations({
			user_id: initiatorUserId,
			type: 'direct',
		});

		const existing = conversations.find((conv) =>
			conv.participants?.some((p) => p.user_id === otherUserId) && !conv.is_closed
		);

		if (existing) {
			return existing;
		}

		// Create new conversation if none exists
		return await createDirectConversation({
			initiator_user_id: initiatorUserId,
			other_user_id: otherUserId,
		});
	} catch (error) {
		console.error('Error finding or creating direct conversation:', error);
		throw error;
	}
};